import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Button,
  Alert,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Label,
  Input,
  FormFeedback,
  Table,
  Badge,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import { Edit, Trash2, Plus, Search, X, Check } from "lucide-react";
import Breadcrumbs from "../../../Components/Common/Breadcrumb";

const baseUrl = process.env.REACT_APP_BASE_URL;

interface CustomPricing {
  id: number;
  customer_id: string;
  customer_name: string;
  customer_email: string;
  product_group_id: number;
  product_group_domain: number;
  product_group_domain_name?: string;
  products: Array<{
    id: number;
    title: string;
    description?: string;
    product_type: string;
    base_price: number | null;
  }>;
  price: number;
  stripe_price_id: string | null;
  active: boolean;
  created_at: string;
  updated_at: string;
  is_in_use: boolean;
}

// Custom Select Component
interface CustomSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: Array<{ id: string | number; label: string; description?: string }>;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

const CustomSelect: React.FC<CustomSelectProps> = ({
  value,
  onChange,
  options,
  placeholder = "Select an option",
  disabled = false,
  className = "",
}) => {
  return (
    <select
      className={`form-control ${className}`}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
    >
      <option value="">{placeholder}</option>
      {options.map((option) => (
        <option key={option.id} value={option.id}>
          {option.label}
        </option>
      ))}
    </select>
  );
};

const CustomPricingManagement = () => {
  document.title = "Custom Pricing Management | BizServe";

  const [customPricings, setCustomPricings] = useState<CustomPricing[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [alertMsgg, setAlertMsgg] = useState<any>({
    open: false,
    type: "success",
    content: "Messages here.",
    time: "2500"
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedPricing, setSelectedPricing] = useState<CustomPricing | null>(null);
  const [customers, setCustomers] = useState<any[]>([]);
  const [productGroups, setProductGroups] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterActive, setFilterActive] = useState<boolean | null>(true);
  const [partnerId, setPartnerId] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    customer_email: "",
    customer_id: "",
    product_group_id: "",
    price: "",
  });
  const [verifiedCustomer, setVerifiedCustomer] = useState<any>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationError, setVerificationError] = useState<string>("");

  const handleAlertMsggClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === "clickaway") {
      return;
    }
    setAlertMsgg({ ...alertMsgg, open: false });
  };

  const fetchCustomPricings = async () => {
    try {
      setIsLoading(true);
      const params: any = {};
      // Only add active filter if it's not null (null means "All")
      if (filterActive !== null) {
        params.active = filterActive.toString();
      }
      // When filterActive is null, don't send active parameter to get all records

      const response = await axios.get(
        `${baseUrl}/payments/manage_custom_pricing/`,
        { params, withCredentials: true }
      );

      // Response body is directly on response object
      if (response.status === "SENT") {
        setCustomPricings(response.custom_pricings || []);
        
        // Extract unique customers from custom pricings
        const uniqueCustomers = new Map();
        (response.custom_pricings || []).forEach((cp: any) => {
          if (!uniqueCustomers.has(cp.customer_id)) {
            uniqueCustomers.set(cp.customer_id, {
              id: cp.customer_id,
              name: cp.customer_name,
              email: cp.customer_email,
            });
          }
        });
        setCustomers(Array.from(uniqueCustomers.values()));
      }
    } catch (error: any) {
      console.error("Error fetching custom pricings:", error);
      const errorMessage = error.response?.error || error.message || "Unknown error";
      console.error("Error details:", errorMessage);
      if (error.response) {
        if ([401, 422].includes(error.response.status)) {
          setAlertMsgg({
            ...alertMsgg,
            open: true,
            content: error.response.error || "Error loading custom pricings",
            type: "error"
          });
        } else if (error.response.status === 403) {
          setAlertMsgg({
            ...alertMsgg,
            open: true,
            content: "Session expired!",
            type: "error"
          });
          setTimeout(() => { window.location.href = "/logout"; }, 2000);
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Removed fetchCustomers - we now use verifyCustomerByEmail instead
  // Customers are populated from custom pricings when they're fetched

  const fetchPartnerId = async () => {
    try {
      const response = await axios.get(
        `${baseUrl}/accounts/get_partner_settings`,
        { withCredentials: true }
      );
      
      // Response body is directly on response object
      if (response.status === "SENT" && response.partner) {
        setPartnerId(response.partner.id?.toString() || null);
        return response.partner.id?.toString() || null;
      }
    } catch (error) {
      console.error("Error fetching partner ID:", error);
    }
    return null;
  };

  const fetchProductGroups = async () => {
    try {
      // Get partner ID and domain first
      let currentPartnerId = partnerId;
      let partnerData: any = null;
      
      if (!currentPartnerId) {
        // Fetch partner settings to get both ID and domain
        try {
          const partnerResponse = await axios.get(
            `${baseUrl}/accounts/get_partner_settings`,
            { withCredentials: true }
          );
          
          if (partnerResponse.status === "SENT" && partnerResponse.partner) {
            currentPartnerId = partnerResponse.partner.id?.toString() || null;
            partnerData = partnerResponse.partner;
            setPartnerId(currentPartnerId);
          }
        } catch (error) {
          console.error("Error fetching partner settings:", error);
        }
      }
      
      if (!currentPartnerId) {
        console.error("Partner ID not found");
        return;
      }

      // Get domain from partner's domains (first one) or URL or use default
      let domainId = "1";
      if (partnerData && partnerData.domains && Array.isArray(partnerData.domains) && partnerData.domains.length > 0) {
        // Use the first domain the partner belongs to
        domainId = partnerData.domains[0].toString();
      } else {
        // Fallback to URL or default
        const urlParams = new URLSearchParams(window.location.search);
        domainId = urlParams.get("domain") || "1";
      }
      
      console.log(`Fetching products for domain: ${domainId}, partner: ${currentPartnerId}`);
      
      // Fetch products with null/0 prices assigned to this partner
      const response = await axios.get(
        `${baseUrl}/payments/listDomainProducts`,
        { params: { domain: domainId, partner: currentPartnerId }, withCredentials: true }
      );
      
      // Response body is directly on response object
      if (response.status === "SENT") {
        // Extract unique product groups from all product types
        // Backend now filters to only return products with null price when partner param is provided
        const allProducts = [
          ...(response.Plan || []),
          ...(response.AddOn || []),
          ...(response.SubscriptionAddOn || []),
          ...(response.PlanOptionals || []),
          ...(response.OneTimeProduct || [])
        ];

        // Get unique product groups with title, description, and domain from first product
        const uniqueGroups = new Map();
        allProducts.forEach((p: any) => {
          if (p.product_group_id && !uniqueGroups.has(p.product_group_id)) {
            uniqueGroups.set(p.product_group_id, {
              id: p.product_group_id,
              title: p.title,
              description: p.description || "",
              domain_name: p.domain_name || "",
              products: []
            });
          }
          if (p.product_group_id) {
            const existingGroup = uniqueGroups.get(p.product_group_id);
            // Update domain_name if not set and this product has it
            if (!existingGroup.domain_name && p.domain_name) {
              existingGroup.domain_name = p.domain_name;
            }
            existingGroup.products.push({
              id: p.id,
              title: p.title,
              description: p.description || "",
              product_type: p.product_type || "plan",
              base_price: p.price,
              validity: p.validity || null,
              period: p.period || null
            });
          }
        });
        setProductGroups(Array.from(uniqueGroups.values()));
      }
    } catch (error) {
      console.error("Error fetching product groups:", error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      // Fetch partner ID first, then fetch product groups (which needs partner ID)
      await fetchPartnerId();
      await fetchCustomPricings();
      await fetchProductGroups();
      // Removed fetchCustomers - we now use verifyCustomerByEmail instead
    };
    loadData();
  }, [filterActive]);

  const handleOpenModal = (pricing?: CustomPricing) => {
    if (pricing) {
      setSelectedPricing(pricing);
      setFormData({
        customer_email: pricing.customer_email,
        customer_id: pricing.customer_id.toString(),
        product_group_id: pricing.product_group_id.toString(),
        price: pricing.price.toString(),
      });
      setVerifiedCustomer({
        id: pricing.customer_id,
        name: pricing.customer_name,
        email: pricing.customer_email,
      });
      setVerificationError("");
    } else {
      setSelectedPricing(null);
      setFormData({
        customer_email: "",
        customer_id: "",
        product_group_id: "",
        price: "",
      });
      setVerifiedCustomer(null);
      setVerificationError("");
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedPricing(null);
    setFormData({
      customer_email: "",
      customer_id: "",
      product_group_id: "",
      price: "",
    });
    setVerifiedCustomer(null);
    setVerificationError("");
  };

  const handleVerifyEmail = async () => {
    const email = formData.customer_email.trim();
    
    if (!email) {
      setVerificationError("Please enter an email address");
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setVerificationError("Please enter a valid email address");
      return;
    }

    try {
      setIsVerifying(true);
      setVerificationError("");
      
      const response = await axios.get(
        `${baseUrl}/payments/verifyCustomerByEmail`,
        { params: { email }, withCredentials: true }
      );

      // Response body is directly on response object
      if (response.exists && response.customer) {
        setVerifiedCustomer(response.customer);
        setFormData({ ...formData, customer_id: response.customer.id.toString() });
        setVerificationError("");
      } else {
        setVerifiedCustomer(null);
        setVerificationError(response.error || "No customer found with this email");
        setFormData({ ...formData, customer_id: "" });
      }
    } catch (error: any) {
      // Handle 404 specifically - customer not found
      if (error.response?.status === 404) {
        // Response body is directly on response object (no .data)
        const errorMsg = error.response?.error || "No customer found with this email address. Please verify the email and try again.";
        setVerificationError(errorMsg);
      } else if (error.response) {
        // Response body is directly on response object (no .data)
        const errorMsg = error.response?.error || error.response?.message || "An error occurred while verifying the email. Please try again.";
        setVerificationError(errorMsg);
      } else if (error.message && !error.message.includes("status code")) {
        // Only use error.message if it's not a generic HTTP error
        setVerificationError(error.message);
      } else {
        // Generic error message
        setVerificationError("An error occurred while verifying the email. Please try again.");
      }
      setVerifiedCustomer(null);
      setFormData({ ...formData, customer_id: "" });
    } finally {
      setIsVerifying(false);
    }
  };

  const handleSubmit = async () => {
    try {
      // Validation: Email must be verified
      if (!verifiedCustomer || !formData.customer_id) {
        setAlertMsgg({
          ...alertMsgg,
          open: true,
          content: "Please verify the customer email before creating custom pricing",
          type: "error"
        });
        return;
      }

      // Validation: Product must be selected
      if (!formData.product_group_id) {
        setAlertMsgg({
          ...alertMsgg,
          open: true,
          content: "Please select a product",
          type: "error"
        });
        return;
      }

      // Validation: Price must be provided
      if (!formData.price || formData.price.trim() === "") {
        setAlertMsgg({
          ...alertMsgg,
          open: true,
          content: "Please enter a custom price",
          type: "error"
        });
        return;
      }

      const priceFloat = parseFloat(formData.price);
      if (isNaN(priceFloat) || priceFloat <= 0) {
        setAlertMsgg({
          ...alertMsgg,
          open: true,
          content: "Price must be a positive number",
          type: "error"
        });
        return;
      }

      setIsLoading(true);
      let response;
      if (selectedPricing) {
        // Update
        response = await axios.put(
          `${baseUrl}/payments/manage_custom_pricing/`,
          {
            custom_pricing_id: selectedPricing.id,
            price: priceFloat,
          },
          { withCredentials: true }
        );
      } else {
        // Create
        response = await axios.post(
          `${baseUrl}/payments/manage_custom_pricing/`,
          {
            customer_id: formData.customer_id,
            product_group_id: parseInt(formData.product_group_id),
            price: priceFloat,
          },
          { withCredentials: true }
        );
      }

      // Response body is directly on response object
      if (response.status === "CREATED" || response.status === "UPDATED") {
        setAlertMsgg({
          ...alertMsgg,
          open: true,
          content: selectedPricing ? "Custom pricing updated successfully" : "Custom pricing created successfully",
          type: "success"
        });
        handleCloseModal();
        fetchCustomPricings();
      }
    } catch (error: any) {
      console.error("Error saving custom pricing:", error);
      if (error.response) {
        setAlertMsgg({
          ...alertMsgg,
          open: true,
          content: error.response.error || "Error saving custom pricing",
          type: "error"
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteClick = (pricing: CustomPricing) => {
    setSelectedPricing(pricing);
    setIsDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedPricing) return;

    try {
      setIsLoading(true);
      const response = await axios.delete(
        `${baseUrl}/payments/manage_custom_pricing/`,
        {
          data: { custom_pricing_id: selectedPricing.id },
          withCredentials: true
        }
      );

      // Response body is directly on response object
      if (response.status === "DELETED") {
        setAlertMsgg({
          ...alertMsgg,
          open: true,
          content: "Custom pricing deleted successfully",
          type: "success"
        });
        setIsDeleteModalOpen(false);
        setSelectedPricing(null);
        fetchCustomPricings();
      }
    } catch (error: any) {
      console.error("Error deleting custom pricing:", error);
      if (error.response) {
        setAlertMsgg({
          ...alertMsgg,
          open: true,
          content: error.response.error || "Error deleting custom pricing",
          type: "error"
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const filteredPricings = customPricings.filter((pricing) => {
    const matchesSearch =
      pricing.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      pricing.customer_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      pricing.products.some((p) => p.title.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesSearch;
  });

  const getSelectedProductGroup = () => {
    return productGroups.find((pg) => pg.id === parseInt(formData.product_group_id));
  };

  const getProductPeriod = (productGroup: any) => {
    if (!productGroup || !productGroup.products || productGroup.products.length === 0) {
      return null;
    }
    const firstProduct = productGroup.products[0];
    // SubscriptionAddOn uses 'period', others use 'validity'
    if (firstProduct.product_type === 'subscriptionaddon') {
      return firstProduct.period;
    }
    return firstProduct.validity;
  };

  const formatPeriod = (period: string | null) => {
    if (!period || period === '' || period === 'lifetime' || period === 'custom') {
      return 'One-time payment';
    }
    const periodMap: { [key: string]: string } = {
      'day': 'per day',
      'week': 'per week',
      'month': 'per month',
      'quarter': 'per quarter',
      'year': 'per year'
    };
    return periodMap[period] || `per ${period}`;
  };

  const getProductTypeBadgeColor = (productType: string) => {
    const type = productType.toLowerCase();
    if (type === "plan") return "primary";
    if (type === "addon") return "info";
    if (type === "subscriptionaddon") return "success";
    if (type === "planoptionals") return "warning";
    if (type === "onetimeproduct") return "secondary";
    return "light";
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Custom Pricing" breadcrumbItem="Manage Custom Pricing" />

          {/* Alert - positioned at top with proper styling */}
          {alertMsgg.open && (
            <Alert
              color={alertMsgg.type === "success" ? "success" : "danger"}
              toggle={handleAlertMsggClose}
              fade={false}
              style={{ fontWeight: "bold", marginBottom: "1rem" }}
            >
              {alertMsgg.content}
            </Alert>
          )}

          <Row>
            <Col lg="12">
              <Card>
                <CardHeader>
                  <Row className="align-items-center">
                    <Col md="6">
                      <h4 className="card-title mb-0">Custom Pricing Management</h4>
                    </Col>
                    <Col md="6" className="text-end">
                      <Button
                        color="primary"
                        onClick={() => handleOpenModal()}
                        className="me-2 d-inline-flex align-items-center"
                      >
                        <Plus size={16} className="me-1" />
                        Add Custom Pricing
                      </Button>
                    </Col>
                  </Row>
                </CardHeader>
                <CardBody>
                  {/* Filters */}
                  <Row className="mb-3">
                    <Col md="6">
                      <div className="d-flex align-items-center">
                        <Search size={16} className="me-2" />
                        <Input
                          type="text"
                          placeholder="Search by customer name, email, or product..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          style={{ maxWidth: "400px" }}
                        />
                      </div>
                    </Col>
                    <Col md="6" className="text-end">
                      <UncontrolledDropdown>
                        <DropdownToggle caret color="secondary">
                          {filterActive === null
                            ? "All"
                            : filterActive
                            ? "Active Only"
                            : "Inactive Only"}
                        </DropdownToggle>
                        <DropdownMenu>
                          <DropdownItem onClick={() => setFilterActive(true)}>
                            Active Only
                          </DropdownItem>
                          <DropdownItem onClick={() => setFilterActive(false)}>
                            Inactive Only
                          </DropdownItem>
                          <DropdownItem onClick={() => setFilterActive(null)}>
                            All
                          </DropdownItem>
                        </DropdownMenu>
                      </UncontrolledDropdown>
                    </Col>
                  </Row>

                  {/* Table */}
                  {isLoading ? (
                    <div className="text-center py-5">
                      <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <Table className="table-nowrap align-middle mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Customer</th>
                            <th>Product</th>
                            <th>Custom Price</th>
                            <th>Status</th>
                            <th>In Use</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredPricings.length === 0 ? (
                            <tr>
                              <td colSpan={6} className="text-center py-4">
                                No custom pricing found
                              </td>
                            </tr>
                          ) : (
                            filteredPricings.map((pricing) => {
                              const firstProduct = pricing.products[0];
                              return (
                                <tr key={pricing.id}>
                                  <td>
                                    <div>
                                      <strong className="text-capitalize">{pricing.customer_name}</strong>
                                      <br />
                                      <small className="text-muted">{pricing.customer_email}</small>
                                    </div>
                                  </td>
                                  <td>
                                    <div>
                                      <div className="d-flex align-items-center gap-2 mb-1">
                                        <strong>{firstProduct?.title || "N/A"}</strong>
                                        {firstProduct?.product_type && (
                                          <Badge color={getProductTypeBadgeColor(firstProduct.product_type)} className="bg-light text-dark border text-capitalize">
                                            {firstProduct.product_type}
                                          </Badge>
                                        )}
                                        {pricing.product_group_domain_name && (
                                          <Badge color="secondary" className="bg-light text-dark border text-capitalize">
                                            {pricing.product_group_domain_name}
                                          </Badge>
                                        )}
                                      </div>
                                      {firstProduct?.description && (
                                        <small className="text-muted d-block">
                                          {firstProduct.description}
                                        </small>
                                      )}
                                    </div>
                                  </td>
                                  <td>
                                    <strong>${pricing.price.toFixed(2)}</strong>
                                  </td>
                                  <td>
                                    {pricing.active ? (
                                      <Badge color="success">Active</Badge>
                                    ) : (
                                      <Badge color="secondary">Inactive</Badge>
                                    )}
                                  </td>
                                  <td>
                                    {pricing.is_in_use ? (
                                      <Badge color="warning">Yes</Badge>
                                    ) : (
                                      <Badge color="light">No</Badge>
                                    )}
                                  </td>
                                  <td>
                                    <div className="d-flex gap-2">
                                      <Button
                                        color="primary"
                                        size="sm"
                                        onClick={() => handleOpenModal(pricing)}
                                      >
                                        <Edit size={14} />
                                      </Button>
                                      <Button
                                        color="danger"
                                        size="sm"
                                        onClick={() => handleDeleteClick(pricing)}
                                        disabled={pricing.is_in_use}
                                      >
                                        <Trash2 size={14} />
                                      </Button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Create/Edit Modal */}
      <Modal isOpen={isModalOpen} toggle={handleCloseModal} size="lg">
        <ModalHeader toggle={handleCloseModal}>
          {selectedPricing ? "Edit Custom Pricing" : "Add Custom Pricing"}
        </ModalHeader>
        <ModalBody>
          <Row>
            <Col md="12" className="mb-3">
              <Label>Customer Email *</Label>
              <div className="d-flex gap-2 align-items-start">
                <div className="flex-grow-1">
                  <Input
                    type="email"
                    value={formData.customer_email}
                    onChange={(e) => {
                      setFormData({ ...formData, customer_email: e.target.value, customer_id: "" });
                      setVerifiedCustomer(null);
                      setVerificationError("");
                    }}
                    placeholder="Enter customer email"
                    disabled={!!selectedPricing}
                    invalid={!!verificationError && !verifiedCustomer && formData.customer_email.trim() !== ""}
                    valid={!!verifiedCustomer}
                  />
                  {verificationError && !verifiedCustomer && formData.customer_email.trim() !== "" && (
                    <FormFeedback>{verificationError}</FormFeedback>
                  )}
                  {verifiedCustomer && (
                    <div className="text-success small mt-1 d-flex align-items-center gap-1">
                      <Check size={14} />
                      <span>User is valid: <span className="text-capitalize">{verifiedCustomer.name || verifiedCustomer.email}</span></span>
                    </div>
                  )}
                  {!verifiedCustomer && !verificationError && formData.customer_email.trim() !== "" && (
                    <div className="text-muted small mt-1">Click "Check" to verify the customer email</div>
                  )}
                </div>
                <Button
                  color="primary"
                  onClick={handleVerifyEmail}
                  disabled={isVerifying || !!selectedPricing || !formData.customer_email.trim()}
                  style={{ minWidth: "80px", marginTop: "0" }}
                >
                  {isVerifying ? "Checking..." : "Check"}
                </Button>
              </div>
            </Col>
            <Col md="12" className="mb-3">
              <Label>Product *</Label>
              <CustomSelect
                value={formData.product_group_id}
                onChange={(value) => setFormData({ ...formData, product_group_id: value })}
                options={productGroups.map((pg) => {
                  const domainText = pg.domain_name ? ` - ${pg.domain_name}` : "";
                  return {
                    id: pg.id,
                    label: `${pg.title || `Product Group #${pg.id}`}${domainText}`
                  };
                })}
                placeholder="Select Product"
                disabled={!!selectedPricing}
              />
              {formData.product_group_id && getSelectedProductGroup() && (
                <div className="mt-2">
                  <div className="mb-2">
                    <div className="d-flex align-items-center gap-2 mb-1">
                      <strong>{getSelectedProductGroup()?.title}</strong>
                      {getSelectedProductGroup()?.products[0]?.product_type && (
                        <Badge color={getProductTypeBadgeColor(getSelectedProductGroup()?.products[0]?.product_type)} className="bg-light text-dark border">
                          {getSelectedProductGroup()?.products[0]?.product_type}
                        </Badge>
                      )}
                    </div>
                    {getSelectedProductGroup()?.description && (
                      <div className="text-muted small mt-1">
                        {getSelectedProductGroup()?.description}
                      </div>
                    )}
                    {getProductPeriod(getSelectedProductGroup()) && (
                      <div className="text-info small mt-1 fw-semibold">
                        Billing Period: {formatPeriod(getProductPeriod(getSelectedProductGroup()))}
                      </div>
                    )}
                  </div>
                  {getSelectedProductGroup()?.products.length > 1 && (
                    <>
                      <small className="text-muted">Products in this group:</small>
                      <div className="mt-1">
                        {getSelectedProductGroup()?.products.map((product: any, idx: number) => (
                          <Badge key={idx} color="info" className="me-1">
                            {product.title} ({product.product_type})
                          </Badge>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
            </Col>
            <Col md="12" className="mb-3">
              <Label>Custom Price ($) *</Label>
              <Input
                type="number"
                step="0.01"
                min="0.01"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                placeholder="Enter custom price"
              />
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={handleCloseModal}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleSubmit} disabled={isLoading}>
            {isLoading ? "Saving..." : selectedPricing ? "Update" : "Create"}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteModalOpen} toggle={() => setIsDeleteModalOpen(false)}>
        <ModalHeader toggle={() => setIsDeleteModalOpen(false)}>
          Confirm Delete
        </ModalHeader>
        <ModalBody>
          {selectedPricing?.is_in_use ? (
            <Alert color="warning">
              This custom pricing is currently in use and cannot be deleted. Customers have active purchases using this pricing.
            </Alert>
          ) : (
            <p>
              Are you sure you want to delete the custom pricing for{" "}
              <strong>{selectedPricing?.customer_name}</strong>? This action cannot be undone.
            </p>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setIsDeleteModalOpen(false)}>
            Cancel
          </Button>
          {!selectedPricing?.is_in_use && (
            <Button color="danger" onClick={handleDelete} disabled={isLoading}>
              {isLoading ? "Deleting..." : "Delete"}
            </Button>
          )}
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default CustomPricingManagement;
